/**
 * MonitoringPage – System-Dashboard fuer Olympus
 * Zeigt: VPS-Health, MongoDB, GPU-Worker, Ollama, Re-Crawl, Logs, Errors
 * Auto-Refresh alle 10 Sekunden
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Activity, Cpu, HardDrive, Database, Zap, Brain, RefreshCw,
  AlertTriangle, CheckCircle2, XCircle, Clock, FileText, Server,
  Mail, Scale, Bot, Users, Calendar, MessageSquare, TrendingUp, Inbox
} from 'lucide-react';

const REFRESH_INTERVAL_MS = 10000;
const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

async function fetchJson(path) {
  const token = localStorage.getItem('olympus_token');
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
}

function StatusPill({ status }) {
  const map = {
    ok: { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'OK' },
    offline: { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', label: 'Offline' },
    error: { icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200', label: 'Fehler' },
    kein_job: { icon: Clock, color: 'text-slate-500 bg-slate-50 border-slate-200', label: 'Kein Job' },
    wartend: { icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Wartend' },
    laeuft: { icon: Activity, color: 'text-blue-600 bg-blue-50 border-blue-200', label: 'Laeuft' },
    fertig: { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Fertig' },
    fehler: { icon: AlertTriangle, color: 'text-red-600 bg-red-50 border-red-200', label: 'Fehler' },
  };
  const cfg = map[status] || { icon: AlertTriangle, color: 'text-slate-500 bg-slate-50 border-slate-200', label: status || '-' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
}

function Card({ title, icon: Icon, status, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-slate-700">
          {Icon && <Icon className="w-5 h-5" />}
          <h3 className="font-semibold">{title}</h3>
        </div>
        {status && <StatusPill status={status} />}
      </div>
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium ${accent || 'text-slate-800'}`}>{value ?? '-'}</span>
    </div>
  );
}

function Bar({ percent, color = 'bg-blue-500' }) {
  const p = Math.max(0, Math.min(100, percent || 0));
  let barColor = color;
  if (p > 90) barColor = 'bg-red-500';
  else if (p > 75) barColor = 'bg-amber-500';
  return (
    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
      <div className={`h-full ${barColor} transition-all`} style={{ width: `${p}%` }} />
    </div>
  );
}

export default function MonitoringPage() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [logFilter, setLogFilter] = useState('');
  const [historicalMode, setHistoricalMode] = useState(false);
  const [historyHours, setHistoryHours] = useState(12);
  const [historyLevel, setHistoryLevel] = useState('');
  const [historyTotal, setHistoryTotal] = useState(0);

  const fetchAll = useCallback(async () => {
    try {
      const [status, logsRes, errorsRes] = await Promise.all([
        fetchJson('/api/monitoring/status'),
        historicalMode
          ? fetchJson(`/api/monitoring/logs-history?since_hours=${historyHours}&limit=5000${historyLevel ? `&level=${historyLevel}` : ''}${logFilter ? `&search=${encodeURIComponent(logFilter)}` : ''}`)
          : fetchJson('/api/monitoring/logs?lines=50'),
        fetchJson('/api/monitoring/errors?lines=20'),
      ]);
      setData(status);
      setLogs(logsRes.lines || []);
      setHistoryTotal(logsRes.count_total_in_window || 0);
      setErrors(errorsRes.errors || []);
      setLastUpdate(new Date());
    } catch (e) {
      console.error('Monitoring fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [historicalMode, historyHours, historyLevel, logFilter]);

  useEffect(() => {
    fetchAll();
    if (!autoRefresh) return;
    const id = setInterval(fetchAll, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchAll, autoRefresh]);

  if (loading && !data) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          Lade System-Status...
        </div>
      </div>
    );
  }

  const backend = data?.backend || {};
  const mongodb = data?.mongodb || {};
  const vps = data?.vps || {};
  const gpu = data?.gpu_worker || {};
  const ollama = data?.ollama || {};
  const recrawl = data?.recrawl || {};
  const vorg = data?.vorgaenge || {};
  const fmtDateTime = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
  };
  const relativeTime = (iso) => {
    if (!iso) return '-';
    try {
      const diff = (Date.now() - new Date(iso).getTime()) / 1000;
      if (diff < 60) return `vor ${Math.round(diff)} s`;
      if (diff < 3600) return `vor ${Math.round(diff / 60)} min`;
      if (diff < 86400) return `vor ${Math.round(diff / 3600)} h`;
      return `vor ${Math.round(diff / 86400)} d`;
    } catch { return iso; }
  };

  const filteredLogs = logFilter
    ? logs.filter(l => l.msg?.toLowerCase().includes(logFilter.toLowerCase()) || l.logger?.toLowerCase().includes(logFilter.toLowerCase()))
    : logs;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="monitoring-page">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Activity className="w-6 h-6" />
            System-Monitoring
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Letztes Update: {lastUpdate ? lastUpdate.toLocaleTimeString('de-DE') : '...'} |
            Auto-Refresh alle {REFRESH_INTERVAL_MS / 1000}s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              data-testid="monitoring-auto-refresh"
            />
            Auto-Refresh
          </label>
          <button
            onClick={fetchAll}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-md hover:bg-slate-50"
            data-testid="monitoring-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Jetzt aktualisieren
          </button>
        </div>
      </div>

      {/* Grid: Systeme */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {/* Backend */}
        <Card title="Backend" icon={Server} status={backend.status} testid="monitoring-backend-card">
          <Row label="Uptime" value={backend.uptime_human} />
          <Row label="Log-Zeilen im Puffer" value={backend.log_buffer_size} />
          <Row label="Fehler im Puffer" value={backend.error_buffer_size} accent={backend.error_buffer_size > 0 ? 'text-red-600' : 'text-emerald-600'} />
        </Card>

        {/* MongoDB */}
        <Card title="MongoDB" icon={Database} status={mongodb.status} testid="monitoring-mongo-card">
          <Row label="Ping" value={`${mongodb.ping_ms ?? '-'} ms`} />
          <Row label="Datenbank-Groesse" value={mongodb.db_size} />
          <Row label="Storage" value={mongodb.storage_size} />
          <Row label="Collections" value={mongodb.collections} />
          <Row label="Objekte" value={mongodb.objects?.toLocaleString('de-DE')} />
        </Card>

        {/* VPS */}
        <Card title="VPS (Hetzner)" icon={Cpu} status={vps.status} testid="monitoring-vps-card">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Disk</span>
              <span className="font-medium">{vps.disk?.used} / {vps.disk?.total} ({vps.disk?.used_percent}%)</span>
            </div>
            <Bar percent={vps.disk?.used_percent} />
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">RAM</span>
              <span className="font-medium">{vps.memory?.used} / {vps.memory?.total} ({vps.memory?.used_percent}%)</span>
            </div>
            <Bar percent={vps.memory?.used_percent} />
          </div>
          <Row label="CPU" value={`${vps.cpu_percent ?? '-'} %`} />
          <Row label="Load (1/5/15)" value={`${vps.load_avg_1min ?? '-'} / ${vps.load_avg_5min ?? '-'} / ${vps.load_avg_15min ?? '-'}`} />
        </Card>

        {/* GPU Worker */}
        <Card title="GPU-Worker (Windows)" icon={Zap} status={gpu.status} testid="monitoring-gpu-card">
          <Row label="URL" value={<code className="text-xs">{gpu.url}</code>} />
          {gpu.reachable ? (
            <>
              <Row label="GPU" value={gpu.gpu} />
              <Row label="VRAM frei" value={gpu.vram_frei_gb ? `${gpu.vram_frei_gb} GB` : '-'} />
              <Row label="OCR-Modus" value={gpu.paddleocr_modus} />
              {gpu.statistik && (
                <>
                  <Row label="OCR-Requests" value={gpu.statistik.ocr_requests} />
                  <Row label="OCR-Fehler" value={gpu.statistik.ocr_error} accent={gpu.statistik.ocr_error > 0 ? 'text-red-600' : ''} />
                </>
              )}
            </>
          ) : (
            <div className="text-xs text-red-600">
              Offline oder nicht erreichbar. Tailscale auf Windows-PC pruefen, `start_olympus.ps1` laufen lassen.
              {gpu.error && (
                <div className="mt-1 p-1.5 bg-red-50 rounded font-mono text-[10px]">
                  {gpu.error}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Ollama */}
        <Card title="Ollama (Windows)" icon={Brain} status={ollama.status} testid="monitoring-ollama-card">
          <Row label="URL" value={<code className="text-xs">{ollama.url}</code>} />
          {ollama.reachable ? (
            <>
              <Row label="Modelle installiert" value={ollama.models_installed?.join(', ') || '-'} />
              <Row
                label="Im VRAM aktiv"
                value={ollama.models_loaded_in_vram?.join(', ') || <span className="text-amber-600">Keins</span>}
                accent={ollama.models_loaded_in_vram?.length ? 'text-emerald-600' : 'text-amber-600'}
              />
            </>
          ) : (
            <div className="text-xs text-red-600">Offline — Ollama-Server auf Windows-PC starten.</div>
          )}
        </Card>

        {/* Re-Crawl */}
        <Card title="Re-Crawl Job" icon={RefreshCw} status={recrawl.status} testid="monitoring-recrawl-card">
          {recrawl.status === 'kein_job' ? (
            <div className="text-xs text-slate-500">Kein aktiver Re-Crawl-Job.</div>
          ) : (
            <>
              <Row label="Job-ID" value={<code className="text-xs">{recrawl.job_id?.slice(0, 8)}...</code>} />
              <Row label="Aktueller Klient" value={recrawl.aktueller_klient} />
              <Row label="Schritt" value={recrawl.aktueller_schritt} />
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-500">Klienten</span>
                  <span>{recrawl.klienten_verarbeitet}/{recrawl.klienten_gesamt} ({recrawl.klienten_fortschritt_prozent}%)</span>
                </div>
                <Bar percent={recrawl.klienten_fortschritt_prozent} color="bg-emerald-500" />
              </div>
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-500">Dateien</span>
                  <span>{recrawl.dateien_verarbeitet}/{recrawl.dateien_gesamt} ({recrawl.dateien_fortschritt_prozent}%)</span>
                </div>
                <Bar percent={recrawl.dateien_fortschritt_prozent} color="bg-blue-500" />
              </div>
              {recrawl.fehler_anzahl > 0 && (
                <Row label="Fehler" value={recrawl.fehler_anzahl} accent="text-red-600" />
              )}
              {recrawl.letzter_fehler && (
                <div className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded">
                  {recrawl.letzter_fehler}
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* ============== VORGÄNGE / HINTERGRUND-PROZESSE ============== */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3 text-slate-700">
          <TrendingUp className="w-5 h-5" />
          <h2 className="text-lg font-semibold">Hintergrund-Vorgänge</h2>
          <span className="text-xs text-slate-400">alle Prozesse, Jobs und geplanten Aufgaben</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Thunderbird-Import */}
          <Card
            title="Thunderbird-Import"
            icon={Mail}
            status={vorg.thunderbird?.status}
            testid="monitoring-thunderbird-card"
          >
            {vorg.thunderbird?.status === 'kein_job' ? (
              <div className="text-xs text-slate-500">Noch kein MBOX-Import gestartet.</div>
            ) : (
              <>
                <Row label="Start" value={fmtDateTime(vorg.thunderbird?.gestartet_am)} />
                <Row label="Aktuelle MBOX" value={vorg.thunderbird?.aktuelle_mbox} />
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500">Emails</span>
                    <span>{vorg.thunderbird?.emails_verarbeitet}/{vorg.thunderbird?.emails_gesamt || '?'} ({vorg.thunderbird?.fortschritt_prozent}%)</span>
                  </div>
                  <Bar percent={vorg.thunderbird?.fortschritt_prozent} color="bg-indigo-500" />
                </div>
                <Row label="Zugeordnet" value={vorg.thunderbird?.emails_zugeordnet} accent="text-emerald-600" />
                <Row label="Unzugeordnet" value={vorg.thunderbird?.emails_unzugeordnet} accent="text-amber-600" />
                <Row label="Anhänge neu / gesamt" value={`${vorg.thunderbird?.anhaenge_neu || 0} / ${vorg.thunderbird?.anhaenge_gesamt || 0}`} />
                <Row label="Neue Kontakte" value={vorg.thunderbird?.kontakte_neu_angelegt} />
                {vorg.thunderbird?.letzter_fehler && (
                  <div className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">{vorg.thunderbird.letzter_fehler}</div>
                )}
              </>
            )}
          </Card>

          {/* Scheduler */}
          <Card
            title="Scheduler-Jobs"
            icon={Calendar}
            status={vorg.scheduler?.status}
            testid="monitoring-scheduler-card"
          >
            {vorg.scheduler?.status !== 'ok' ? (
              <div className="text-xs text-amber-600">Scheduler nicht aktiv.</div>
            ) : (
              <div className="space-y-1.5">
                {(vorg.scheduler.jobs || []).map(j => (
                  <div key={j.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-700 truncate">{j.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono truncate">{j.trigger}</div>
                    </div>
                    <div className="text-right ml-2 text-slate-500 text-[11px]">
                      <div>{fmtDateTime(j.next_run)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* E-Mail-Abruf */}
          <Card
            title="E-Mail-Abruf (alle 30 Min)"
            icon={Inbox}
            status={vorg.email_fetch?.status === 'kein_lauf' ? 'kein_job' : vorg.email_fetch?.status}
            testid="monitoring-email-fetch-card"
          >
            {vorg.email_fetch?.status === 'kein_lauf' ? (
              <div className="text-xs text-amber-600">Noch kein E-Mail-Abruf protokolliert. Der nächste Lauf läuft automatisch in &lt;30 Min.</div>
            ) : (
              <>
                <Row label="Letzter Lauf" value={relativeTime(vorg.email_fetch?.letzter_lauf)} />
                <Row label="Konto" value={vorg.email_fetch?.letzter_konto} />
                <Row label="Neue Mails (letzter Lauf)" value={vorg.email_fetch?.letzte_neue_emails} />
                {vorg.email_fetch?.letzter_fehler && (
                  <div className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">{vorg.email_fetch.letzter_fehler}</div>
                )}
                {vorg.email_fetch?.['24h_stats'] && Object.keys(vorg.email_fetch['24h_stats']).length > 0 && (
                  <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
                    24h: {Object.entries(vorg.email_fetch['24h_stats']).map(([k, v]) => `${k}=${v.count}`).join(', ')}
                  </div>
                )}
              </>
            )}
          </Card>

          {/* EGVP */}
          <Card
            title="EGVP (Gericht)"
            icon={Scale}
            status={vorg.egvp?.status}
            testid="monitoring-egvp-card"
          >
            <Row label="Posteingang ungelesen" value={vorg.egvp?.posteingang_ungelesen} accent={vorg.egvp?.posteingang_ungelesen > 0 ? 'text-amber-600' : 'text-slate-800'} />
            <Row label="Unbearbeitet" value={vorg.egvp?.posteingang_unbearbeitet} accent={vorg.egvp?.posteingang_unbearbeitet > 0 ? 'text-amber-600' : 'text-slate-800'} />
            <Row label="Letzte Aktion" value={vorg.egvp?.letzte_aktion} />
            {vorg.egvp?.letzter_ts && <Row label="Zuletzt" value={relativeTime(vorg.egvp.letzter_ts)} />}
          </Card>

          {/* KI-Fristenüberwachung */}
          <Card
            title="KI-Fristenüberwachung"
            icon={Bot}
            status={vorg.ki_ueberwachung?.status === 'kein_lauf' ? 'kein_job' : vorg.ki_ueberwachung?.status}
            testid="monitoring-ki-card"
          >
            {vorg.ki_ueberwachung?.status === 'kein_lauf' ? (
              <div className="text-xs text-amber-600">Noch kein KI-Lauf protokolliert. Nächster automatischer Lauf: täglich 8:05.</div>
            ) : (
              <>
                <Row label="Letzter Lauf" value={relativeTime(vorg.ki_ueberwachung?.letzter_ts)} />
                <Row label="Aufgaben erstellt" value={vorg.ki_ueberwachung?.aufgaben_erstellt} />
                <Row label="Entwürfe erstellt" value={vorg.ki_ueberwachung?.entwuerfe_erstellt} />
                {vorg.ki_ueberwachung?.letzter_fehler && (
                  <div className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">{vorg.ki_ueberwachung.letzter_fehler}</div>
                )}
              </>
            )}
          </Card>

          {/* Entity-Resolver / Reviews */}
          <Card
            title="Entity-Resolver"
            icon={Users}
            status={vorg.entity_resolver?.status}
            testid="monitoring-entity-card"
          >
            <Row label="Reviews gesamt" value={vorg.entity_resolver?.reviews_gesamt} />
            <Row
              label="Offene Reviews"
              value={vorg.entity_resolver?.reviews_offen}
              accent={vorg.entity_resolver?.reviews_offen > 0 ? 'text-amber-600' : 'text-emerald-600'}
            />
            {vorg.entity_resolver?.letzter_lauf && <Row label="Letzter Lauf" value={relativeTime(vorg.entity_resolver.letzter_lauf)} />}
          </Card>

          {/* Wochenbriefing */}
          <Card
            title="Wochenbriefing"
            icon={FileText}
            status={vorg.wochen_briefing?.status === 'kein_briefing' ? 'kein_job' : vorg.wochen_briefing?.status}
            testid="monitoring-briefing-card"
          >
            {vorg.wochen_briefing?.status === 'kein_briefing' ? (
              <div className="text-xs text-slate-500">Noch kein Briefing erstellt.</div>
            ) : (
              <>
                <Row label="Kalenderwoche" value={vorg.wochen_briefing?.kalenderwoche ? `KW ${vorg.wochen_briefing.kalenderwoche}` : '-'} />
                <Row label="Erstellt" value={relativeTime(vorg.wochen_briefing?.erstellt_am)} />
              </>
            )}
            <div className="text-[11px] text-slate-400 mt-2 pt-2 border-t border-slate-100">
              Auto-Versand: Montag 7:30
            </div>
          </Card>

          {/* Monatsprotokoll */}
          <Card
            title="Monatsprotokoll"
            icon={Calendar}
            status={vorg.monatsprotokoll?.status === 'kein_versand' ? 'kein_job' : vorg.monatsprotokoll?.status}
            testid="monitoring-monatsprotokoll-card"
          >
            {vorg.monatsprotokoll?.status === 'kein_versand' ? (
              <div className="text-xs text-slate-500">Noch kein Versand protokolliert.</div>
            ) : (
              <>
                <Row label="Letzter Versand" value={relativeTime(vorg.monatsprotokoll?.letzter_versand)} />
                <Row label="Empfänger" value={vorg.monatsprotokoll?.empfaenger_count} />
              </>
            )}
            <div className="text-[11px] text-slate-400 mt-2 pt-2 border-t border-slate-100">
              Auto-Versand: 1. des Monats, 8:00
            </div>
          </Card>

          {/* WhatsApp */}
          <Card
            title="WhatsApp"
            icon={MessageSquare}
            status={vorg.whatsapp?.verbunden ? 'ok' : 'offline'}
            testid="monitoring-whatsapp-card"
          >
            <Row label="Verbunden" value={vorg.whatsapp?.verbunden ? 'Ja' : 'Nein'} accent={vorg.whatsapp?.verbunden ? 'text-emerald-600' : 'text-amber-600'} />
            <Row label="Status" value={vorg.whatsapp?.status} />
            {vorg.whatsapp?.letzter_ts && <Row label="Zuletzt" value={relativeTime(vorg.whatsapp.letzter_ts)} />}
          </Card>
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="mb-6 bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden" data-testid="monitoring-errors-card">
          <div className="bg-red-50 px-5 py-3 border-b border-red-200 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-red-700">Aktuelle Fehler ({errors.length})</h3>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {errors.map((err, i) => (
              <div key={i} className="px-5 py-2 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-slate-400">{new Date(err.ts).toLocaleTimeString('de-DE')}</span>
                  <span className="text-slate-400">|</span>
                  <span className="text-slate-600 font-medium">{err.logger}</span>
                </div>
                <div className="text-red-700 font-mono whitespace-pre-wrap">{err.msg}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live Logs */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="monitoring-logs-card">
        <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-700">
              {historicalMode
                ? `Log-Historie (${logs.length}/${historyTotal} in ${historyHours}h)`
                : `Live-Logs (letzte ${logs.length})`}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={historicalMode}
                onChange={e => setHistoricalMode(e.target.checked)}
                data-testid="monitoring-historical-toggle"
              />
              Historie
            </label>
            {historicalMode && (
              <>
                <select
                  value={historyHours}
                  onChange={e => setHistoryHours(parseInt(e.target.value))}
                  className="px-2 py-1 text-sm border border-slate-200 rounded-md bg-white"
                  data-testid="monitoring-history-hours"
                >
                  <option value="1">1h</option>
                  <option value="6">6h</option>
                  <option value="12">12h</option>
                  <option value="24">24h</option>
                  <option value="48">48h (max)</option>
                </select>
                <select
                  value={historyLevel}
                  onChange={e => setHistoryLevel(e.target.value)}
                  className="px-2 py-1 text-sm border border-slate-200 rounded-md bg-white"
                  data-testid="monitoring-history-level"
                >
                  <option value="">Alle Level</option>
                  <option value="INFO">INFO</option>
                  <option value="WARNING">WARNING</option>
                  <option value="ERROR">ERROR</option>
                </select>
              </>
            )}
            <input
              type="text"
              placeholder="Filter/Suche..."
              value={logFilter}
              onChange={e => setLogFilter(e.target.value)}
              className="px-3 py-1 text-sm border border-slate-200 rounded-md"
              data-testid="monitoring-log-filter"
            />
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-slate-50 bg-slate-900 text-slate-200 font-mono text-xs">
          {filteredLogs.length === 0 ? (
            <div className="p-5 text-center text-slate-500">Keine Log-Eintraege.</div>
          ) : (
            filteredLogs.map((log, i) => {
              const levelColor =
                log.level === 'ERROR' ? 'text-red-400' :
                log.level === 'WARNING' ? 'text-amber-300' :
                'text-slate-400';
              return (
                <div key={i} className="px-4 py-1 hover:bg-slate-800">
                  <span className="text-slate-500">{new Date(log.ts).toLocaleTimeString('de-DE')}</span>{' '}
                  <span className={levelColor}>[{log.level}]</span>{' '}
                  <span className="text-cyan-300">{log.logger}</span>
                  <span className="text-slate-500"> - </span>
                  <span className="whitespace-pre-wrap">{log.msg}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
